import { getIndividualPropertySuiteByID } from 'src/services/client';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { ISuite } from 'src/components/SharedTypes';

interface UsePropertySuitesParams {
  propertyId: string;
  userId: string;
  enabled?: boolean;
}

interface UsePropertySuitesReturn {
  suites: ISuite[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const usePropertySuites = ({
  propertyId,
  userId,
  enabled = true,
}: UsePropertySuitesParams): UsePropertySuitesReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const [suites, setSuites] = useState<ISuite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuites = async () => {
    if (!enabled || !propertyId || !userId) return;

    try {
      setIsLoading(true);
      setError(null);

      const userRole = user?.roles[0]?.role?.name;

      if (!userRole) {
        setError('User role not found');
        return;
      }

      const response: any = await getIndividualPropertySuiteByID(
        propertyId,
        userId,
        userRole
      );

      if (!response || !response.ok) {
        console.error('Failed to fetch property suites by ID:', response);
        setError('Failed to load suites');
        return;
      }

      const suitesFromAPI: any = await response.json();

      if (!suitesFromAPI || !Array.isArray(suitesFromAPI)) {
        setError('Invalid response format');
        return;
      }

      setSuites(suitesFromAPI);
    } catch (err) {
      console.error('Error fetching suites:', err);
      setError('Error loading suites');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuites();
  }, [propertyId, userId, user?.roles, enabled]);

  return {
    suites,
    isLoading,
    error,
    refetch: fetchSuites,
  };
};
